/**
 * Admin configuration area — request/response DTOs (docs/API.md admin
 * section). Covers users, teams, departments, locations, the ticket
 * catalogue (categories/subcategories/priorities/impact/urgency/severity/
 * resolution categories), SLA (policies/targets/business calendars/windows/
 * holidays), routing rules and the admin audit log.
 *
 * Same two rules as `ticket-commands.ts` apply here:
 *
 *  1. Actor identity is never accepted from a body. `actorId` for the
 *     `AdminAuditLog` row the service writes always comes from the session.
 *  2. A schema here validates SHAPE only. Cross-row rules that need a
 *     database read (a referenced team/category exists and is active and in
 *     the same org; a role removal would leave the org without an ADMIN)
 *     are enforced in the admin services, not here.
 */
import { z } from 'zod';
import { ALL_PERMISSIONS, ROLES } from './permissions.js';
const requiredText = () => z.string().trim().min(1);
const optionalCode = () => z.string().trim().min(1).max(64);
const trimmedName = () => z.string().trim().min(1).max(200);
// ---------------------------------------------------------------------------
// Paging — offset pagination, deliberately NOT the cursor scheme
// `ticketListQueryDtoSchema` uses.
//
// Admin config lists (users aside, which can run to a few hundred rows) are
// small, bounded, low-churn tables an operator browses and jumps around in
// ("page 3", "sort by name") rather than a live queue mutating under the
// reader. Cursor pagination is the right tool for the ticket queue
// (docs/API.md §3) specifically because concurrent writes make offset
// pagination skip/duplicate rows there; that risk is negligible for a
// catalogue of priorities or teams, and page numbers are what the admin UI's
// `Pagination` component (apps/web/src/ui/Pagination) already expects.
// ---------------------------------------------------------------------------
export const pagedQueryDtoSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().min(1).optional(),
});
// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
export const ADMIN_USER_STATUSES = ['ACTIVE', 'DISABLED', 'PENDING_INVITE'];
/**
 * Where a user record came from. `ENTRA` iff the row is linked to an Entra ID
 * object id (created or linked by directory sync, or linked at sign-in);
 * `LOCAL` otherwise. Derived, never stored — the Entra object id itself is
 * never exposed by the admin API.
 */
export const ADMIN_USER_SOURCES = ['ENTRA', 'LOCAL'];
export const adminUserListQueryDtoSchema = pagedQueryDtoSchema.extend({
    status: z.enum(ADMIN_USER_STATUSES).optional(),
    departmentId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    /** A system role code or a custom role's code. */
    role: z.lazy(() => roleCodeSchema).optional(),
    source: z.enum(ADMIN_USER_SOURCES).optional(),
});
/**
 * Creates a local directory record (no Entra object id — that is linked
 * later, its own audited step per docs/SECURITY.md §6). `roles` must be
 * non-empty: a user with no role can authenticate but do nothing, which is
 * confusing support debt rather than a meaningful "no access" state.
 */
export const adminCreateUserDtoSchema = z.object({
    email: z.string().trim().email().max(320),
    displayName: trimmedName(),
    departmentId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    roles: z.array(z.enum(ROLES)).min(1),
    /** Required when `roles` includes ADMIN — see `adminRoleChangeReasonSchema`. */
    reason: z.string().trim().min(1).max(500).optional(),
});
export const adminUpdateUserDtoSchema = z.object({
    displayName: trimmedName().optional(),
    status: z.enum(ADMIN_USER_STATUSES).optional(),
    departmentId: z.string().uuid().nullable().optional(),
    locationId: z.string().uuid().nullable().optional(),
});
export const adminAssignRoleDtoSchema = z.object({ role: z.enum(ROLES) });
/**
 * `PUT /admin/users/:id/role` — REPLACES the user's whole role set with
 * exactly this one role (the admin UI's single "role" picker). The
 * last-active-admin guard applies. `POST/DELETE /admin/users/:id/roles` remain
 * for multi-role management.
 */
/**
 * Why an ADMIN grant/removal is happening. Required by the server whenever a
 * role change adds or removes ADMIN (see `requireAdminChangeReason`), and
 * stored as the admin-audit row's `reason`. Ignored-but-accepted otherwise.
 */
export const adminRoleChangeReasonSchema = z.string().trim().min(1).max(500);
/** A system role code (`ADMIN`, ...) or a custom role's code (`CUSTOM_...`). */
export const roleCodeSchema = z
    .string()
    .trim()
    .refine((v) => ROLES.includes(v) || /^CUSTOM_[A-Z0-9]{1,56}$/.test(v), 'Unknown role.');
export const adminSetUserRoleDtoSchema = z.object({ role: roleCodeSchema, reason: adminRoleChangeReasonSchema.optional() }).strict();
// ---------------------------------------------------------------------------
// Directory sync (Entra ID -> users). docs/ENTRA.md "Directory sync".
// ---------------------------------------------------------------------------
export const DIRECTORY_SYNC_SOURCES = ['graph', 'fixture', 'disabled'];
export const DIRECTORY_SYNC_TRIGGERS = ['ADMIN_LOGIN', 'MANUAL', 'SCHEDULED'];
export const DIRECTORY_SYNC_RUN_STATUSES = ['RUNNING', 'SUCCEEDED', 'FAILED'];
// ---------------------------------------------------------------------------
// Live Entra ID lookup (Admin → Teams → "Add a person"): search the directory
// for people the nightly sync has not brought in yet, and import one.
// ---------------------------------------------------------------------------
export const DIRECTORY_SEARCH_MIN = 2;
export const DIRECTORY_SEARCH_MAX = 100;
export const DIRECTORY_SEARCH_LIMIT = 10;
/** `GET /admin/directory-sync/search?q=` — a name or email fragment. */
export const directorySearchQueryDtoSchema = z
    .object({
    q: z
        .string()
        .transform((v) => v.trim())
        .pipe(z.string().min(DIRECTORY_SEARCH_MIN, 'Type at least 2 characters.').max(DIRECTORY_SEARCH_MAX)),
})
    .strict();
/** `POST /admin/directory-sync/import` — create (or link) the helpdesk account for one directory person. */
export const directoryImportDtoSchema = z.object({ objectId: z.string().uuid() }).strict();
export const adminSetUserTeamDtoSchema = z.object({
    teamId: z.string().uuid(),
    isLead: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------
export const adminCreateTeamDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    email: z.string().trim().email().max(320).nullish(),
    isActive: z.boolean().default(true),
});
export const adminUpdateTeamDtoSchema = z.object({
    name: trimmedName().optional(),
    email: z.string().trim().email().max(320).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const adminAddTeamMemberDtoSchema = z.object({
    userId: z.string().uuid(),
    isLead: z.boolean().default(false),
});
export const adminUpdateTeamMemberDtoSchema = z.object({ isLead: z.boolean() });
// ---------------------------------------------------------------------------
// Directory: departments, locations
// ---------------------------------------------------------------------------
export const adminCreateDepartmentDtoSchema = z.object({
    name: trimmedName(),
    code: optionalCode().nullish(),
});
export const adminUpdateDepartmentDtoSchema = z.object({
    name: trimmedName().optional(),
    code: optionalCode().nullable().optional(),
});
export const adminCreateLocationDtoSchema = z.object({
    name: trimmedName(),
    site: z.string().trim().max(200).nullish(),
    floor: z.string().trim().max(50).nullish(),
});
export const adminUpdateLocationDtoSchema = z.object({
    name: trimmedName().optional(),
    site: z.string().trim().max(200).nullable().optional(),
    floor: z.string().trim().max(50).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Catalogue: categories, subcategories, priorities, impact/urgency/severity,
// resolution categories
// ---------------------------------------------------------------------------
export const adminCreateCategoryDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    defaultTeamId: z.string().uuid().nullish(),
    sortOrder: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
});
export const adminUpdateCategoryDtoSchema = z.object({
    name: trimmedName().optional(),
    defaultTeamId: z.string().uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
});
export const adminCreateSubcategoryDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    isActive: z.boolean().default(true),
});
export const adminUpdateSubcategoryDtoSchema = z.object({
    name: trimmedName().optional(),
    isActive: z.boolean().optional(),
});
export const adminCreatePriorityDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    rank: z.number().int().min(1),
    colourToken: z.string().trim().max(50).nullish(),
});
export const adminUpdatePriorityDtoSchema = z.object({
    name: trimmedName().optional(),
    rank: z.number().int().min(1).optional(),
    colourToken: z.string().trim().max(50).nullable().optional(),
});
/** Shared by impact, urgency and severity — the three lookup tables share an identical (code, name, rank) shape. */
export const adminCreateRankedLookupDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    rank: z.number().int().min(1),
});
export const adminUpdateRankedLookupDtoSchema = z.object({
    name: trimmedName().optional(),
    rank: z.number().int().min(1).optional(),
});
export const ADMIN_RANKED_LOOKUP_KINDS = ['impact', 'urgency', 'severity'];
export const adminCreateResolutionCategoryDtoSchema = z.object({
    code: optionalCode(),
    name: trimmedName(),
    isActive: z.boolean().default(true),
});
export const adminUpdateResolutionCategoryDtoSchema = z.object({
    name: trimmedName().optional(),
    isActive: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// SLA: policies, targets, business calendars, hour windows, holidays
// ---------------------------------------------------------------------------
export const SLA_TARGET_KINDS = ['FIRST_RESPONSE', 'RESOLUTION', 'CLOSURE'];
export const adminCreateSlaPolicyDtoSchema = z.object({
    name: trimmedName(),
    priorityId: z.string().uuid().nullish(),
    categoryId: z.string().uuid().nullish(),
    departmentId: z.string().uuid().nullish(),
    calendarId: z.string().uuid().nullish(),
    precedence: z.number().int().min(0).default(0),
    pauseOnPending: z.boolean().default(true),
    isActive: z.boolean().default(true),
    /**
     * Project brief §20 "escalation thresholds"
     * (20260923340000_sla_auto_escalation). When true, the SLA monitor sweep
     * (apps/worker/src/jobs/sla-monitor.job.ts) escalates a ticket the moment
     * this policy's RESOLUTION target transitions to BREACHED.
     */
    escalateOnBreach: z.boolean().default(false),
    /** Optional reassignment target for that escalation; null/omitted records the escalation without moving the ticket. */
    escalationTeamId: z.string().uuid().nullish(),
});
export const adminUpdateSlaPolicyDtoSchema = z.object({
    name: trimmedName().optional(),
    priorityId: z.string().uuid().nullable().optional(),
    categoryId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    calendarId: z.string().uuid().nullable().optional(),
    precedence: z.number().int().min(0).optional(),
    pauseOnPending: z.boolean().optional(),
    isActive: z.boolean().optional(),
    escalateOnBreach: z.boolean().optional(),
    escalationTeamId: z.string().uuid().nullable().optional(),
});
/**
 * Mirrors the admin brief's validation rule ("SLA targets: positive minutes,
 * warning < target"). `warningPercent` is a percentage of `targetMinutes`
 * (matching `SlaTarget.warningPercent`'s existing semantics elsewhere in the
 * codebase), so `warningPercent < 100` is what makes the warning threshold
 * strictly earlier than the target — enforced by `.refine` below rather than
 * left to the service, so a bad request fails as `400 VALIDATION_ERROR`
 * before any database work.
 */
export const adminUpsertSlaTargetDtoSchema = z
    .object({
    kind: z.enum(SLA_TARGET_KINDS),
    targetMinutes: z.number().int().min(1),
    warningPercent: z.number().int().min(1).max(99).default(80),
})
    .refine((v) => v.warningPercent < 100, { message: 'warningPercent must be less than 100', path: ['warningPercent'] });
export const adminCreateBusinessCalendarDtoSchema = z.object({
    name: trimmedName(),
    timezone: z.string().trim().min(1).max(100).default('Asia/Kolkata'),
});
export const adminUpdateBusinessCalendarDtoSchema = z.object({
    name: trimmedName().optional(),
    timezone: z.string().trim().min(1).max(100).optional(),
});
export const adminUpsertBusinessHourWindowDtoSchema = z
    .object({
    dayOfWeek: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
})
    .refine((v) => v.startMinute < v.endMinute, { message: 'startMinute must be before endMinute', path: ['endMinute'] });
export const adminCreateHolidayDtoSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
    name: trimmedName(),
});
// ---------------------------------------------------------------------------
// Routing rules
// ---------------------------------------------------------------------------
export const TICKET_SOURCE_VALUES = ['WEB', 'EMAIL', 'API', 'PHONE', 'IMPORT'];
const routingRuleFields = {
    name: trimmedName(),
    precedence: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
    matchCategoryId: z.string().uuid().nullish(),
    /** Multi-category match; when non-empty it replaces `matchCategoryId`. */
    matchCategoryIds: z.array(z.string().uuid()).max(20).optional(),
    /** Users every matching ticket is assigned to (first = lead, rest co-assignees). */
    assigneeUserIds: z.array(z.string().uuid()).max(25).optional(),
    matchDepartmentId: z.string().uuid().nullish(),
    matchLocationId: z.string().uuid().nullish(),
    matchPriorityId: z.string().uuid().nullish(),
    matchSource: z.enum(TICKET_SOURCE_VALUES).nullish(),
    matchKeyword: z.string().trim().min(1).max(200).nullish(),
    assignTeamId: z.string().uuid().nullish(),
    assignTechnicianId: z.string().uuid().nullish(),
    useRoundRobin: z.boolean().default(false),
    setPriorityId: z.string().uuid().nullish(),
};
export const adminCreateRoutingRuleDtoSchema = z.object(routingRuleFields).refine((v) => Boolean(v.assignTeamId || v.assignTechnicianId || v.setPriorityId || v.useRoundRobin || (v.assigneeUserIds && v.assigneeUserIds.length > 0)), { message: 'a routing rule must do at least one of: assign a team, assign a technician, set a priority, or use round robin', path: ['assignTeamId'] });
export const adminUpdateRoutingRuleDtoSchema = z.object({
    name: trimmedName().optional(),
    precedence: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    matchCategoryId: z.string().uuid().nullable().optional(),
    matchCategoryIds: z.array(z.string().uuid()).max(20).optional(),
    assigneeUserIds: z.array(z.string().uuid()).max(25).optional(),
    matchDepartmentId: z.string().uuid().nullable().optional(),
    matchLocationId: z.string().uuid().nullable().optional(),
    matchPriorityId: z.string().uuid().nullable().optional(),
    matchSource: z.enum(TICKET_SOURCE_VALUES).nullable().optional(),
    matchKeyword: z.string().trim().min(1).max(200).nullable().optional(),
    assignTeamId: z.string().uuid().nullable().optional(),
    assignTechnicianId: z.string().uuid().nullable().optional(),
    useRoundRobin: z.boolean().optional(),
    setPriorityId: z.string().uuid().nullable().optional(),
});
/**
 * `POST /admin/routing-rules/dry-run` — "which rule would match this sample
 * ticket" (admin brief). Shape mirrors the `match*` fields a rule can key
 * off; every field is optional because a dry run may want to test "what if
 * this ticket has no department at all", which is itself a meaningful case.
 */
export const adminRoutingDryRunDtoSchema = z.object({
    categoryId: z.string().uuid().optional(),
    departmentId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    priorityId: z.string().uuid().optional(),
    source: z.enum(TICKET_SOURCE_VALUES).optional(),
    keyword: z.string().trim().max(300).optional(),
});
/** `GET /admin/routing-rules/assignee-search?q=&limit=` — empty `q` lists the whole eligible directory (up to `limit`, max 500). */
export const adminAssigneeSearchQuerySchema = z.object({
    q: z.string().trim().max(100).default(''),
    limit: z.coerce.number().int().min(1).max(500).default(500),
});
// ---------------------------------------------------------------------------
// System settings — a typed allow-list, never arbitrary key/value writes
// (admin brief: "system settings (a typed allow-list of keys, never
// arbitrary key/value writes)"). Every key here is one an existing service
// already reads (`apps/api/src/modules/{confirmation,email,tickets}/**`) —
// this is the admin surface for values that were previously only settable by
// hand in the database.
// ---------------------------------------------------------------------------
export const ADMIN_SETTING_DEFS = {
    'ticket.reopen_window_days': {
        type: 'number',
        min: 1,
        max: 365,
        description: 'How many days after a ticket is closed the requester can still reopen it.',
    },
    'email.action_token_ttl_days': {
        type: 'number',
        min: 1,
        max: 90,
        description: 'How many days the "Yes, it is fixed" / "No, still broken" links in resolution emails keep working.',
    },
    'email.max_inbound_per_sender_5m': {
        type: 'number',
        min: 1,
        max: 1000,
        description: 'The most emails one sender can send to the helpdesk in 5 minutes before further mail is held (protects against mail loops and floods).',
    },
    'confirmation_page.show_title': {
        type: 'boolean',
        description: 'Show the ticket title on the page requesters open from the confirmation email (off keeps titles private if an email is forwarded).',
    },
    'email_deletion.closure_fixed_cc': {
        type: 'email',
        description: 'Mandatory CC on every Email Deletion closure email. Email Deletion tickets cannot be closed until this is set.',
    },
    'email_deletion.closure_cc_max_additional': {
        type: 'number',
        min: 0,
        max: 25,
        description: 'How many extra CC addresses a technician may add when closing an Email Deletion ticket (default 10).',
    },
};
export const ADMIN_SETTING_KEYS = Object.keys(ADMIN_SETTING_DEFS);
export const adminSettingKeySchema = z.enum(ADMIN_SETTING_KEYS);
export const adminUpdateSettingDtoSchema = z.object({
    // A string only for `type: 'email'` keys; the service checks the key's type.
    value: z.union([z.number(), z.boolean(), z.string().max(320)]),
});
// ---------------------------------------------------------------------------
// Notification-preference defaults (admin brief: "notification-preference
// defaults"). Stored as a single `SystemSetting` row (key below) rather than
// a new table — it is org-wide configuration, not a per-user preference (the
// existing `NotificationPreference` model, owned by the notifications
// module), applied as the starting point the notifications module falls back
// to for a user who has not set an explicit preference of their own.
// ---------------------------------------------------------------------------
export const NOTIFICATION_DEFAULTS_SETTING_KEY = 'notifications.defaults';
export const adminNotificationDefaultDtoSchema = z.object({
    eventType: z.string().trim().min(1).max(100),
    channel: z.enum(['EMAIL', 'IN_APP', 'WEBHOOK', 'TEAMS', 'PUSH']),
    enabled: z.boolean(),
});
export const adminUpdateNotificationDefaultsDtoSchema = z.object({
    defaults: z.array(adminNotificationDefaultDtoSchema).min(1).max(200),
});
// ---------------------------------------------------------------------------
// Admin audit log — GET /admin/audit-log
// ---------------------------------------------------------------------------
export const adminAuditLogQueryDtoSchema = pagedQueryDtoSchema.extend({
    entityType: z.string().trim().min(1).max(100).optional(),
    entityId: z.string().trim().min(1).max(200).optional(),
    actorId: z.string().uuid().optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
export const ADMIN_ROLE_STATUSES = ['ACTIVE', 'DISABLED'];
const roleName = () => z.string().trim().min(2).max(80);
const roleDescription = () => z.string().trim().max(500);
const permissionCodes = () => z
    .array(z.enum(ALL_PERMISSIONS))
    .max(ALL_PERMISSIONS.length)
    .transform((list) => [...new Set(list)].sort());
export const adminCreateRoleDtoSchema = z
    .object({
    name: roleName(),
    description: roleDescription().optional(),
    baseRole: z.enum(ROLES),
    permissions: permissionCodes(),
})
    .strict();
export const adminCloneRoleDtoSchema = z.object({ name: roleName(), description: roleDescription().optional() }).strict();
/** `baseRole` is fixed at creation (it decides what the database sees for every member); clone the role to build on a different level. */
export const adminUpdateRoleDtoSchema = z
    .object({
    name: roleName().optional(),
    description: roleDescription().nullable().optional(),
    permissions: permissionCodes().optional(),
    status: z.enum(ADMIN_ROLE_STATUSES).optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change.' });
export const adminRoleMemberAddDtoSchema = z.object({ userId: z.string().uuid(), reason: adminRoleChangeReasonSchema.optional() }).strict();
//# sourceMappingURL=admin-dto.js.map